import React, {useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';

import search from '../../lib/search';
import SideModal from '../../components/SideModal';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import Placeholder from '../../components/Placeholder';

import {REUSE_MODES, getReuseMode, getReuseModeLabel, canBeSharedAnswerSource, canLinkSource} from './shared';

// Two steps, like importing a question: choose a form, then choose one of its source questions. Rendered into the body
// because it is opened from inside a question card, which clips and blurs its content.
function SharedAnswerSourcePicker({forms, question, linkedSourceIds, onLink, onClose}) {
  const [formFilter, setFormFilter] = useState('');
  const [questionFilter, setQuestionFilter] = useState('');
  const [reuseModeFilter, setReuseModeFilter] = useState('');
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);

  // Archived forms never offer their answers, and a form with no source questions has nothing to pick, so both are left
  // out. A company can have hundreds of forms and only a few sources.
  const candidatesByForm = useMemo(
    () => forms
      .filter(form => form.current !== false)
      .map(form => ({form, candidates: getSourceCandidates(form, question, linkedSourceIds)}))
      .filter(({candidates}) => candidates.length > 0)
      .sort((a, b) => a.form.name.localeCompare(b.form.name)),
    [forms, question, linkedSourceIds]
  );

  const filteredForms = useMemo(
    () => candidatesByForm.filter(({form}) => formFilter === '' || search(form.name, formFilter)),
    [candidatesByForm, formFilter]
  );

  const selected = candidatesByForm.filter(({form}) => form.id === selectedFormId)[0] || null;

  const filteredCandidates = useMemo(
    () => (selected ? selected.candidates : [])
      .filter(source => questionFilter === '' || search(source.text, questionFilter))
      .filter(source => reuseModeFilter === '' || getReuseMode(source) === reuseModeFilter),
    [selected, questionFilter, reuseModeFilter]
  );

  const handleSelectForm = form => {
    setSelectedFormId(form.id);
    setSelectedSource(null);
    setQuestionFilter('');
    setReuseModeFilter('');
  };

  const handleBack = () => {
    setSelectedFormId(null);
    setSelectedSource(null);
  };

  return createPortal(
    <SideModal
      open
      title="Add a Source"
      onClose={onClose}
      options={[
        <Button primary disabled={!selectedSource} onClick={() => onLink(selectedSource, selected.form)}>
          Add Source
        </Button>,
        <Button secondary onClick={onClose}>Cancel</Button>
      ]}>
      <SideModal.Content>
        {!selected ? (
          <>
            <Instructions>
              {question.type === 'INVENTORY_USAGE'
                ? 'Choose the form with the receiving question to draw from.'
                : 'Choose the form with the answers this question should offer.'}
            </Instructions>
            <Filter>
              <Input
                autoFocus
                placeholder="Filter forms..."
                aria-label="Filter forms"
                value={formFilter}
                onChange={e => setFormFilter(e.target.value)}
              />
            </Filter>
            {filteredForms.length === 0 ? (
              <Placeholder height="6rem">
                {candidatesByForm.length === 0
                  ? 'No forms have source questions yet. Turn on "Use as a source for other forms" on a question first.'
                  : 'No forms match your filter.'}
              </Placeholder>
            ) : (
              <List>
                {filteredForms.map(({form, candidates}) => (
                  <li key={form.id}>
                    <ListButton onClick={() => handleSelectForm(form)}>
                      <ItemText>{form.name}</ItemText>
                      <ItemDetail>{plural(candidates.length, 'source question')}</ItemDetail>
                      <Icon name="chevron-right" />
                    </ListButton>
                  </li>
                ))}
              </List>
            )}
          </>
        ) : (
          <>
            <Button tertiary onClick={handleBack}>
              <Icon name="chevron-left" /> All forms
            </Button>
            <FormName>{selected.form.name}</FormName>
            <QuestionFilters>
              <Input
                autoFocus
                placeholder="Filter questions..."
                aria-label="Filter questions"
                value={questionFilter}
                onChange={e => setQuestionFilter(e.target.value)}
              />
              <Input
                type="select"
                aria-label="Filter by reuse mode"
                value={reuseModeFilter}
                onChange={e => setReuseModeFilter(e.target.value)}>
                <option value="">Any reuse mode</option>
                {REUSE_MODES.map(mode => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </Input>
            </QuestionFilters>
            {filteredCandidates.length === 0 ? (
              <Placeholder height="6rem">No source questions match your filters.</Placeholder>
            ) : (
              <List role="radiogroup" aria-label={`Source questions on ${selected.form.name}`}>
                {filteredCandidates.map(source => (
                  <li key={source.id}>
                    <QuestionOption>
                      <input
                        type="radio"
                        name={`shared-answer-source-${question.id}`}
                        checked={!!selectedSource && selectedSource.id === source.id}
                        onChange={() => setSelectedSource(source)}
                      />
                      <ItemText>{source.text}</ItemText>
                      <ItemDetail>{getReuseModeLabel(getReuseMode(source))}</ItemDetail>
                    </QuestionOption>
                  </li>
                ))}
              </List>
            )}
          </>
        )}
      </SideModal.Content>
    </SideModal>,
    document.body
  );
}

// The questions on a form that can feed the receiving question: set up as a source, of a type that can be shared and
// paired with it, and not already linked. The receiving question itself is left out.
function getSourceCandidates(form, question, linkedSourceIds) {
  return [...form.contextQuestions, ...form.questions]
    .filter(source => !source.deleted && source.id !== question.id && !linkedSourceIds.includes(source.id))
    .filter(source => canBeSharedAnswerSource(source) && !!getReuseMode(source) && canLinkSource(question, source))
    .sort((a, b) => a.order - b.order);
}

function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

const Instructions = styled.p`
  margin: 0 0 1rem 0;
  color: #747474;
  line-height: 1.5;
`;

const Filter = styled.div`
  margin-bottom: 1rem;
`;

const FormName = styled.h4`
  margin: 1rem 0;
  font-size: 1.125rem;
`;

const QuestionFilters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  > div:first-child {
    flex: 1;
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 0.0625rem solid #e0e5ea;
  border-radius: 0.25rem;

  > li + li {
    border-top: 0.0625rem solid #e0e5ea;
  }
`;

const ListButton = styled(props => <Button {...props} block blank />)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;

  :hover:not(:disabled) {
    background: #f5f5f5;
  }
`;

const QuestionOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;

  :hover {
    background: #f5f5f5;
  }

  input[type=radio] {
    margin: 0;
  }
`;

const ItemText = styled.span`
  flex: 1;
`;

const ItemDetail = styled.span`
  font-size: 0.875rem;
  color: #747474;
  white-space: nowrap;
`;

export default SharedAnswerSourcePicker;
